import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';

import { Modal, Tabs } from 'antd';
import styled from 'styled-components';

import { sendEmailNotification } from 'apis/CommunityAPI';
import { getClients } from 'apis/UserAPI';
import { shareView } from 'apis/ViewAPI';
import { useRootContext } from 'components/layout/RootContext';
import { EMAIL_NOTIFICATION_TYPE } from 'components/Settings/constants';
import { canViewPricingData, isManagedBySuperuser } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { CommunityType } from 'types/communities';

import { SHARE_MODAL_TAB_LABELS, SHARE_MODAL_TABS } from './constants';
import SendEmailNotificationMainContent from './sendEmailNotification/SendEmailNotificationMainContent';
import SendEmailNotificationSidebar from './sendEmailNotification/SendEmailNotificationSidebar';
import ShareViaEmailMainContent from './shareViaEmail/ShareViaEmailMainContent';
import ShareViaEmailSidebar from './shareViaEmail/ShareViaEmailSidebar';
import {
  createDefaultNotificationsTabData,
  createDefaultTabData,
  getTabDataOrDefault,
  ShareViaEmailContext,
  TabData,
} from './ShareViaEmailContext';
import ShareViaEmailFooter from './ShareViaEmailFooter';
import { User } from './types';
import { generateRandomColor } from './utils';
import { STATUS } from '../../../constants';
import { useCommunityViewContext } from '../CommunityViewContext';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 820px;
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 0;
    padding: 0;
  }
  .ant-checkbox-wrapper {
    font-size: 12px;
    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: var(--positive-green);
      border-color: var(--positive-green);
    }
  }
`;

const Container = styled.div`
  flex: 1;
  display: flex;
`;

const HeaderTabs = styled.div`
  font-size: 14px;
  height: 59px;
  border-bottom: 1px solid var(--line-gray);
  padding: 0 24px;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav::before {
    border-bottom: none !important;
  }

  .ant-tabs-nav {
    margin-bottom: 0 !important;
  }

  .ant-tabs-nav-wrap {
    height: 60px;
  }
`;

interface Props {
  open: boolean;
  onClose: () => void;
}

const ShareViaEmailModal = ({ open, onClose }: Props) => {
  const { showMessage } = useRootContext();
  const { viewId } = useParams();
  const { currentUser } = useAppSelector((state) => state.auth);

  const { communityId, competitorId, competitors } = useCommunityViewContext();
  const { selectedCompany, communityMap } = useAppSelector((state) => state.global);

  const [activeTab, setActiveTab] = useState<string>(SHARE_MODAL_TABS.SHARE);
  const [notificationType, setNotificationType] = useState<number>(EMAIL_NOTIFICATION_TYPE.MARKET_INTELLIGENCE_DIGEST);
  const [loading, setLoading] = useState<string>(STATUS.IDLE);
  const [usersKeyword, setUsersKeyword] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);

  const [shareTabData, setShareTabData] = useState<TabData>({
    ...createDefaultTabData(),
    views: [Number(viewId)],
  });

  const [notificationsTabData, setNotificationsTabData] = useState<Record<number, TabData>>(
    createDefaultNotificationsTabData()
  );
  const showPricingData = canViewPricingData(currentUser);

  const currentNotificationTabData = getTabDataOrDefault(notificationsTabData?.[notificationType]);

  const updateShareTabData = (updates: Partial<TabData>) => {
    setShareTabData((prev) => ({ ...prev, ...updates }));
  };

  const updateNotificationTabData = (type: number, updates: Partial<TabData>) => {
    setNotificationsTabData((prev) => ({
      ...prev,
      [type]: { ...prev[type], ...updates },
    }));
  };

  const handleClose = () => {
    setShareTabData((prev) => ({ ...prev, views: [Number(viewId)], note: '' }));
    setUsersKeyword('');
    onClose();
  };

  const handleSend = async () => {
    setLoading(STATUS.LOADING);
    const currentData = activeTab === SHARE_MODAL_TABS.SHARE ? shareTabData : currentNotificationTabData;
    const userEmails = currentData.selectedUsers.map((u) => u.email);
    const allEmails = [...userEmails, ...currentData.emails.filter(Boolean)];
    const recipients = [
      ...currentData.selectedUsers.map((u) => ({ id: u.id, email: u.email })),
      ...currentData.emails.filter(Boolean).map((email) => ({ id: null, email })),
    ];

    try {
      if (activeTab === SHARE_MODAL_TABS.SHARE) {
        await shareView({
          community_id: communityId,
          competitor_id: competitorId,
          views: currentData.views,
          emails: allEmails,
          note: currentData.note,
        });
        showMessage('success', 'Community views published.');
      } else {
        await sendEmailNotification(communityId, notificationType, {
          recipients,
          competitor_ids: currentData.selectedCompetitors,
        });
        showMessage('success', 'Email notification sent successfully.');
      }
      setLoading(STATUS.LOADED);
      handleClose();
    } catch {
      setLoading(STATUS.FAILURE);
      showMessage(
        'error',
        activeTab === SHARE_MODAL_TABS.SHARE
          ? 'Trouble publishing community views.'
          : 'Trouble sending email notification.'
      );
    }
  };

  const showNotificationsTab = !competitorId;

  const { data } = useQuery({
    queryKey: ['users', selectedCompany, usersKeyword],
    queryFn: () => getClients({ search: usersKeyword, page_size: 300, company: selectedCompany?.id, is_active: true }),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) setUsers(data.results.map((user: User) => ({ ...user, color: generateRandomColor() })));
  }, [data]);

  useEffect(() => {
    setShareTabData((prev) => ({ ...prev, views: [Number(viewId)] }));
  }, [viewId]);

  useEffect(() => {
    if (
      !showPricingData &&
      (notificationType === EMAIL_NOTIFICATION_TYPE.MARKET_INTELLIGENCE_DIGEST ||
        notificationType === EMAIL_NOTIFICATION_TYPE.SALES_BATTLECARD_DIGEST)
    ) {
      setNotificationType(EMAIL_NOTIFICATION_TYPE.ONLINE_REVIEWS_DIGEST);
    }
  }, [showPricingData, notificationType]);

  const handleUserSearch = (value: string) => setUsersKeyword(value);

  const handleSelect = (id: number) => {
    const selectedUser = users.find((u) => u.id === id);
    if (selectedUser) {
      const updates =
        activeTab === SHARE_MODAL_TABS.SHARE
          ? { selectedUsers: [...shareTabData.selectedUsers, selectedUser] }
          : { selectedUsers: [...currentNotificationTabData.selectedUsers, selectedUser] };
      activeTab === SHARE_MODAL_TABS.SHARE
        ? updateShareTabData(updates)
        : updateNotificationTabData(notificationType, updates);
    }
  };

  const handleSelectAll = () => {
    const currentData = activeTab === SHARE_MODAL_TABS.SHARE ? shareTabData : currentNotificationTabData;
    const updates = { selectedUsers: currentData.selectedUsers.length === users.length ? [] : [...users] };
    activeTab === SHARE_MODAL_TABS.SHARE
      ? updateShareTabData(updates)
      : updateNotificationTabData(notificationType, updates);
  };

  const handleDeselect = (id: number) => {
    const currentData = activeTab === SHARE_MODAL_TABS.SHARE ? shareTabData : currentNotificationTabData;
    const updates = { selectedUsers: currentData.selectedUsers.filter((u) => u.id !== id) };
    activeTab === SHARE_MODAL_TABS.SHARE
      ? updateShareTabData(updates)
      : updateNotificationTabData(notificationType, updates);
  };

  const showSendTestEmailButton = activeTab === SHARE_MODAL_TABS.NOTIFICATION && isManagedBySuperuser(currentUser);

  const tabItems = Object.entries(SHARE_MODAL_TAB_LABELS).filter(
    ([key]) => key !== SHARE_MODAL_TABS.NOTIFICATION || showNotificationsTab
  );

  return (
    <ShareViaEmailContext.Provider
      value={{
        loading,
        activeTab,
        notificationType,
        users,
        shareTabData,
        notificationsTabData,
        updateShareTabData,
        updateNotificationTabData,
        setNotificationType,
        handleClose,
        handleSend,
        handleSelect,
        handleSelectAll,
        handleDeselect,
        handleUserSearch,
      }}
    >
      <StyledModal
        centered
        open={open}
        onCancel={handleClose}
        destroyOnClose
        width="fit-content"
        footer={false}
        title={null}
      >
        <MainContainer>
          <HeaderTabs>
            <StyledTabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setUsersKeyword('');
              }}
              items={tabItems.map(([key, label]) => ({
                key,
                label,
              }))}
            />
          </HeaderTabs>

          <Container>
            {activeTab === SHARE_MODAL_TABS.SHARE ? (
              <>
                <ShareViaEmailSidebar competitors={competitors} />
                <ShareViaEmailMainContent />
              </>
            ) : (
              <>
                <SendEmailNotificationSidebar competitors={competitors} />
                <SendEmailNotificationMainContent />
              </>
            )}
          </Container>

          <ShareViaEmailFooter showSendTestEmailButton={showSendTestEmailButton} />
        </MainContainer>
      </StyledModal>
    </ShareViaEmailContext.Provider>
  );
};

export default ShareViaEmailModal;
